export function makeCancelablePromise<T>(promise: Promise<T>, silent: boolean = true) {
  let hasCanceled = false;

  const wrappedPromise = new Promise<T>((resolve, reject) => {
    promise.then(
      val => (hasCanceled ? !silent && reject({ isCanceled: true }) : resolve(val)),
      error => (hasCanceled ? !silent && reject({ isCanceled: true }) : reject(error))
    );
  });

  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled = true;
    }
  };
}

export async function retryPromise<A>(
  fn: () => Promise<A>,
  retryCount: number,
  retryDelay: number,
  backoffFactor: number = 1.0
): Promise<A> {
  const retryWithTimeout = (error: any) =>
    new Promise<A>((resolve, reject) => {
      if (retryCount > 0) {
        setTimeout(
          () => resolve(retryPromise(fn, retryCount - 1, retryDelay * backoffFactor, backoffFactor)),
          retryDelay
        );
      } else {
        reject(error);
      }
    });

  try {
    return await fn();
  } catch (error) {
    return retryWithTimeout(error);
  }
}

export function waitPromise(delay: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, delay);
  });
}

export function compactPromiseErrors<T>(tasks: Array<Promise<T>>): Promise<T[]> {
  return tasks.reduce<Promise<T[]>>(
    (promiseChain, currentTask) =>
      promiseChain.then(chainResults =>
        currentTask.then(currentResult => [...chainResults, currentResult]).catch((error: any) => chainResults)
      ),
    Promise.resolve([] as T[])
  );
}

interface PromiseQueueOptions {
  autoStart?: boolean;
  concurrency?: number;
}

interface PromiseQueueTaskOptions {
  priority?: number;
}

interface PendingPromiseQueueTask {
  priority: number;
  run: () => Promise<void>;
}

export interface PromiseQueue {
  add: <T>(task: () => PromiseLike<T> | T, options?: PromiseQueueTaskOptions) => Promise<T>;
  start: () => void;
}

export const createPromiseQueue = ({ autoStart = true, concurrency = 1 }: PromiseQueueOptions = {}): PromiseQueue => {
  if (!Number.isSafeInteger(concurrency) || concurrency <= 0) {
    throw new RangeError(`Concurrency must be a positive safe integer, got '${concurrency}'.`);
  }

  const pendingTasks: PendingPromiseQueueTask[] = [];
  let activeTaskCount = 0;
  let isStarted = autoStart;
  let isDispatching = false;

  const processTasks = (): void => {
    if (!isStarted || isDispatching) {
      return;
    }

    isDispatching = true;
    try {
      while (activeTaskCount < concurrency && pendingTasks.length > 0) {
        const task = pendingTasks.shift();
        if (task) {
          activeTaskCount += 1;
          void task.run();
        }
      }
    } finally {
      isDispatching = false;
    }
  };

  const add = <T>(task: () => PromiseLike<T> | T, { priority = 0 }: PromiseQueueTaskOptions = {}): Promise<T> => {
    if (!Number.isFinite(priority)) {
      return Promise.reject(new RangeError(`Priority must be finite, got '${priority}'.`));
    }

    return new Promise<T>((resolve, reject) => {
      const pendingTask: PendingPromiseQueueTask = {
        priority,
        run: async (): Promise<void> => {
          try {
            const result = await new Promise<T>(resolveTask => resolveTask(task()));
            resolve(result);
          } catch (error) {
            reject(error);
          } finally {
            activeTaskCount -= 1;
            processTasks();
          }
        }
      };
      const insertionIndex = pendingTasks.findIndex(pending => pending.priority < priority);
      pendingTasks.splice(insertionIndex === -1 ? pendingTasks.length : insertionIndex, 0, pendingTask);

      processTasks();
    });
  };

  return {
    add,
    start: () => {
      isStarted = true;
      processTasks();
    }
  };
};

export const queueChain = async <V, R>(
  collection: readonly V[],
  concurrency: number,
  iteratee: (item: V, index: number) => Promise<R>
): Promise<R[]> => {
  const items = Array.from(collection);
  const queue = createPromiseQueue({ concurrency });
  return Promise.all(items.map((item, index) => queue.add(() => iteratee(item, index))));
};

export function timeoutPromise<T>(promise: Promise<T>, timeout: number) {
  return timeoutPromiseWithAbortSignal(() => promise, timeout);
}

export async function timeoutPromiseWithAbortSignal<T>(fn: (abortSignal: AbortSignal) => Promise<T>, timeout: number) {
  const controller = new AbortController();

  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.abort();
      reject(new Error("Promised timed out"));
    }, timeout);

    fn(controller.signal).then(
      val => {
        clearTimeout(timer);
        return !controller.signal.aborted && resolve(val);
      },
      error => {
        clearTimeout(timer);
        return !controller.signal.aborted && reject(error);
      }
    );
  });
}
